import { useState, useEffect, useRef } from "react";

// `fetchPage` is called as fetchPage({ page, pageSize }), 1-indexed to match the backend's
// PagedResult, and must resolve to { rows, totalCount }. `page` in this hook's state is
// 0-indexed (GridTable's convention). Any change to `deps` resets to page 0, so a filter
// change can't strand the view past the end of the new result set.
export function usePaginatedList(fetchPage, deps = [], { pageSize: initialPageSize = 10 } = {}) {
  const [page, setPage] = useState(0);
  // `pageSize` lives in state (seeded from the `initialPageSize` option) rather than being
  // read straight off the option every render, so a caller that wires DashboardTable's
  // rows-per-page control up to `setPageSize` (below) can actually change it — DrillModal
  // does exactly this once it moves to server pagination.
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const depsKey = JSON.stringify(deps);
  const prevDepsKey = useRef(depsKey);

  // Wraps the raw setState so every pageSize change also resets to page 0 — the same
  // rule the deps-change branch below already enforces, and the same convention MUI's
  // own DataGrid rows-per-page control follows. Without this, a viewer sitting on (say)
  // page 3 of a 10-per-page listing who switches to 100-per-page would stay on page
  // index 3 — now far past the end of the newly-paged result set — instead of landing
  // back on page 1 to see the larger page. React 18 batches these two setState calls
  // (and the deps effect below only fires once, since both land in the same render).
  const setPageSize = (nextPageSize) => {
    setPageSizeState(nextPageSize);
    setPage(0);
  };

  useEffect(() => {
    if (prevDepsKey.current !== depsKey) {
      prevDepsKey.current = depsKey;
      if (page !== 0) {
        setPage(0);
        return; // the page-reset below will trigger the fetch effect again
      }
    }

    let alive = true;
    setLoading(true);
    setError(null);
    fetchPage({ page: page + 1, pageSize })
      .then((result) => {
        if (!alive) return;
        setRows(result?.rows || []);
        setTotalCount(result?.totalCount || 0);
      })
      .catch((err) => {
        if (alive) setError(err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, depsKey]);

  return { rows, totalCount, page, setPage, pageSize, setPageSize, loading, error };
}
